<script lang="ts">
	/**
	 * The whole settings screen, generated from `GET /api/admin/settings`.
	 *
	 * **Adding a setting must require no change to this file.** That is the property the whole
	 * design rests on: the moment configuring a knob means writing a form, people stop adding
	 * settings and start adding environment variables, and configuration-as-data decays back into
	 * a deployment concern (Design §4.5, D16). So every control here is chosen from the
	 * descriptor's declared `type`, every bound comes from its declared range, and every label and
	 * helper line comes from its `key` and `description`.
	 *
	 * If a setting ever genuinely needs bespoke rendering, add a hint to the descriptor and handle
	 * it below — never a special case keyed on the setting's name.
	 *
	 * The form submits **once**, as a batch: the server validates every key before applying any,
	 * so a screen with one bad field changes nothing rather than half-applying.
	 */

	import { Button, Checkbox, Input, Toggle } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import type { SettingDescriptor } from '../AppTypes';
	import ConfirmButton from './ConfirmButton.svelte';
	import CountrySelect from './CountrySelect.svelte';
	import FormField from './FormField.svelte';
	import NumberListEditor from './NumberListEditor.svelte';
	import Panel from './Panel.svelte';
	import StringListEditor from './StringListEditor.svelte';

	/** The registry as the server describes it. Replaced wholesale after every save. */
	export let settings: SettingDescriptor[] = [];
	/** Disables the controls while a batch is in flight. */
	export let saving: boolean = false;
	/** The key whose Reset is in flight, if any. */
	export let resettingKey: string = '';

	const dispatch = createEventDispatcher<{
		save: { values: Record<string, unknown> };
		reset: { key: string };
	}>();

	/**
	 * Working copy, keyed by setting key. Re-derived whenever the server hands back a new list.
	 *
	 * Deliberately `any`: one map holds a value per declared type — a number here, a `string[]`
	 * there — and every `bind:` below would otherwise need a cast at the binding site, which
	 * `bind:` does not accept.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let drafts: Record<string, any> = {};
	let source: SettingDescriptor[] | null = null;

	$: if (settings !== source) {
		source = settings;
		drafts = Object.fromEntries(settings.map(setting => [setting.key, initialDraft(setting)]));
	}

	/**
	 * A secret's stored value is a mask, never the value — so its draft starts empty and the mask
	 * becomes the placeholder. Anything else would post the mask back as the new secret.
	 */
	function initialDraft(setting: SettingDescriptor): unknown {
		if (setting.isSecret) {
			return '';
		}
		return Array.isArray(setting.value) ? [...setting.value] : setting.value;
	}

	function isChanged(setting: SettingDescriptor): boolean {
		if (setting.isLocked) {
			return false;
		}
		if (setting.isSecret) {
			return String(drafts[setting.key] ?? '').length > 0;
		}
		return JSON.stringify(drafts[setting.key]) !== JSON.stringify(setting.value);
	}

	const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	/**
	 * Client-side validation exists so an out-of-range number is refused before the batch leaves —
	 * not as the authority. The registry schema on the server is the authority, and it runs again.
	 */
	function validate(setting: SettingDescriptor): string {
		const value = drafts[setting.key];

		if (setting.isRequired && !setting.isSecret && isEmpty(value)) {
			return 'This setting is required; the store stays closed until it is set.';
		}

		if (setting.type === 'int') {
			if (value === '' || value === null || value === undefined) {
				return setting.isRequired ? 'A number is required.' : 'Enter a whole number.';
			}
			const parsed = Number(value);
			if (!Number.isInteger(parsed)) {
				return 'Must be a whole number.';
			}
			if (setting.minimum !== null && parsed < setting.minimum) {
				return `Must be ${setting.minimum} or more.`;
			}
			if (setting.maximum !== null && parsed > setting.maximum) {
				return `Must be ${setting.maximum} or less.`;
			}
			return '';
		}

		if (setting.type === 'url' && String(value ?? '').length > 0 && !isURL(String(value))) {
			return 'Must be a full URL, including https://.';
		}

		if (setting.type === 'email' && String(value ?? '').length > 0 && !EMAIL_PATTERN.test(String(value))) {
			return 'Must be an email address.';
		}

		return '';
	}

	function isEmpty(value: unknown): boolean {
		return (
			value === undefined ||
			value === null ||
			value === '' ||
			(Array.isArray(value) && value.length === 0)
		);
	}

	function isURL(value: string): boolean {
		try {
			const url = new URL(value);
			return url.protocol === 'http:' || url.protocol === 'https:';
		} catch {
			return false;
		}
	}

	/** `billing.maxGraceDays` → `Max grace days`. The key is the label; nothing is hand-written. */
	function labelOf(key: string): string {
		const leaf = key.slice(key.indexOf('.') + 1);
		const spaced = leaf.replace(/\./g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
		return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
	}

	/** Acronyms a naive capitalisation would mangle into "Api" and "Url". */
	const GROUP_TITLES: Record<string, string> = { api: 'API' };

	function groupTitle(group: string): string {
		return GROUP_TITLES[group] ?? group.charAt(0).toUpperCase() + group.slice(1);
	}

	function idOf(key: string): string {
		return `setting-${key.replace(/\./g, '-')}`;
	}

	/**
	 * Ticks or unticks one choice of a `choiceList`, keeping the declared order so the saved list does
	 * not depend on the order the boxes were clicked in (and an unchanged selection is not "changed").
	 */
	function toggleChoice(setting: SettingDescriptor, value: string, event: Event): void {
		const checked = (event.currentTarget as HTMLInputElement).checked;
		const current = new Set<string>(Array.isArray(drafts[setting.key]) ? drafts[setting.key] : []);
		if (checked) {
			current.add(value);
		} else {
			current.delete(value);
		}
		drafts[setting.key] = (setting.choices ?? []).map(choice => choice.value).filter(item => current.has(item));
	}

	/**
	 * The value actually sent. `int` inputs bind as strings — submitting `"30"` would round-trip a
	 * number as text and silently skip the declared range on the way in.
	 */
	function submissionValue(setting: SettingDescriptor): unknown {
		const value = drafts[setting.key];
		return setting.type === 'int' ? Number(value) : value;
	}

	// Grouped by key prefix, in the order the registry declared them, so the screen's shape follows
	// the registry rather than an alphabetical accident.
	$: groups = settings.reduce<{ name: string; settings: SettingDescriptor[] }[]>((accumulator, setting) => {
		const name = setting.key.slice(0, setting.key.indexOf('.'));
		const existing = accumulator.find(group => group.name === name);
		if (existing) {
			existing.settings.push(setting);
		} else {
			accumulator.push({ name, settings: [setting] });
		}
		return accumulator;
	}, []);

	$: errors = Object.fromEntries(
		settings
			.filter(setting => !setting.isLocked)
			.map(setting => [setting.key, validate(setting)])
	);
	$: hasErrors = Object.values(errors).some(message => message.length > 0);
	// `drafts` is named so the statement re-runs when a draft is reassigned in a handler (a checkbox
	// toggle); `isChanged` reads it only inside its body, which Svelte's dependency analysis cannot see.
	$: changed = ((_drafts: unknown) => settings.filter(setting => isChanged(setting)))(drafts);
	$: isDirty = changed.length > 0;

	function fnSave(): void {
		if (hasErrors || !isDirty) {
			return;
		}
		dispatch('save', {
			values: Object.fromEntries(changed.map(setting => [setting.key, submissionValue(setting)]))
		});
	}

	function fnDiscard(): void {
		drafts = Object.fromEntries(settings.map(setting => [setting.key, initialDraft(setting)]));
	}
</script>

<div class="space-y-4">
	{#each groups as group (group.name)}
		<Panel title={groupTitle(group.name)} faIcon="sliders">
			<div class="space-y-5">
				{#each group.settings as setting (setting.key)}
					{@const controlID = idOf(setting.key)}
					{@const isReadOnly = setting.isLocked || saving}
					<FormField
						id={controlID}
						label={labelOf(setting.key)}
						required={setting.isRequired}
						description={setting.description}
						error={errors[setting.key] ?? ''}
					>
						<svelte:fragment slot="labelRight">
							<div class="flex items-center gap-2">
								{#if setting.isLocked}
									<!-- Read-only, and it says why: an operator who cannot find the field that
									     will not save has no way to discover the environment tier exists. -->
									<span class="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
										<i class="fa-solid fa-lock mr-1" />Locked by {setting.environmentVariable}
									</span>
								{:else if setting.source === 'database-invalid'}
									<span class="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
										<i class="fa-solid fa-triangle-exclamation mr-1" />Stored value rejected
									</span>
								{:else if setting.source === 'database'}
									<span class="text-xs text-default">Overridden</span>
								{/if}

								{#if !setting.isLocked && (setting.source === 'database' || setting.source === 'database-invalid')}
									<ConfirmButton
										size="xs"
										color="alternative"
										action={`reset ${setting.key} to its code default`}
										disabled={saving || resettingKey === setting.key}
										on:confirm={() => dispatch('reset', { key: setting.key })}
									>
										{#if resettingKey === setting.key}
											<i class="fa-solid fa-spinner fa-spin mr-1" />
										{:else}
											<i class="fa-solid fa-rotate-left mr-1" />
										{/if}
										Reset to default
									</ConfirmButton>
								{/if}
							</div>
						</svelte:fragment>

						{#if setting.type === 'boolean'}
							<!-- A labelled Toggle, never the chromeless `ToggleButton`: an unlabelled dimmed
							     icon for `webhooks.enabled` is hostile to a keyboard and to a screen reader. -->
							<Toggle id={controlID} bind:checked={drafts[setting.key]} disabled={isReadOnly}>
								{drafts[setting.key] ? 'Enabled' : 'Disabled'}
							</Toggle>
						{:else if setting.type === 'int'}
							<Input
								id={controlID}
								type="number"
								step="1"
								min={setting.minimum ?? undefined}
								max={setting.maximum ?? undefined}
								bind:value={drafts[setting.key]}
								disabled={isReadOnly}
							/>
						{:else if setting.type === 'intList'}
							<NumberListEditor
								id={controlID}
								bind:values={drafts[setting.key]}
								minimum={setting.minimum}
								maximum={setting.maximum}
								disabled={isReadOnly}
							/>
						{:else if setting.type === 'country'}
							<!-- Declared on the descriptor, never keyed off the setting name: a branch on
							     `tax.sellerCountry` here is invisible from the registry and stops matching
							     the day the key moves, leaving a text field nobody meant to ship. -->
							<CountrySelect id={controlID} bind:value={drafts[setting.key]} disabled={isReadOnly} />
						{:else if setting.type === 'choiceList'}
							<!-- One-way `checked` plus a handler that reassigns the list: a binding into a
							     record entry would refuse an entry that has never been ticked. -->
							<div id={controlID} class="space-y-2" role="group" aria-label={labelOf(setting.key)}>
								{#each setting.choices ?? [] as choice (choice.value)}
									<Checkbox
										checked={(drafts[setting.key] ?? []).includes(choice.value)}
										disabled={isReadOnly}
										on:change={event => toggleChoice(setting, choice.value, event)}
									>
										{choice.label}
									</Checkbox>
								{/each}
								<p class="text-xs text-default">
									{(drafts[setting.key] ?? []).length === 0
										? 'Nothing ticked: the payment provider decides which methods are offered.'
										: 'Only the ticked methods are offered. Each must also be enabled at the payment provider.'}
								</p>
							</div>
						{:else if setting.type === 'stringList'}
							<StringListEditor
								id={controlID}
								bind:values={drafts[setting.key]}
								disabled={isReadOnly}
								placeHolder="Add a value"
							/>
						{:else if setting.isSecret}
							<!-- Write-only: the stored value is a mask, so an untouched field submits nothing
							     rather than posting the mask back as the new secret. -->
							<Input
								id={controlID}
								type="password"
								autocomplete="off"
								placeholder={String(setting.value ?? '') || 'Not set'}
								bind:value={drafts[setting.key]}
								disabled={isReadOnly}
							/>
						{:else}
							<Input
								id={controlID}
								type={setting.type === 'email' ? 'email' : setting.type === 'url' ? 'url' : 'text'}
								bind:value={drafts[setting.key]}
								disabled={isReadOnly}
							/>
						{/if}

						{#if setting.source === 'database-invalid' && setting.error}
							<p class="text-xs text-red-600 dark:text-red-500 mt-1">
								The stored value was rejected ({setting.error}) and the code default is in effect.
								Reset the setting, or save a valid value.
							</p>
						{/if}
					</FormField>
				{/each}
			</div>
		</Panel>
	{/each}

	<div class="controlpanel flex flex-wrap items-center gap-3">
		<Button color="primary" disabled={saving || !isDirty || hasErrors} on:click={fnSave}>
			{#if saving}
				<i class="fa-solid fa-spinner fa-spin mr-2" />
			{/if}
			Save {changed.length > 0 ? `${changed.length} change${changed.length === 1 ? '' : 's'}` : 'changes'}
		</Button>

		<Button color="alternative" disabled={saving || !isDirty} on:click={fnDiscard}>Discard</Button>

		{#if hasErrors}
			<span class="text-sm text-red-600 dark:text-red-500">
				<i class="fa-solid fa-triangle-exclamation mr-1" />Fix the highlighted fields before saving.
			</span>
		{:else if isDirty}
			<span class="text-sm text-default">Saved as one batch: nothing applies unless every field validates.</span>
		{/if}
	</div>
</div>
